import React, { useState, useEffect, useCallback, useRef } from 'react';
import { connect } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom/cjs/react-router-dom.min';
import { FiSearch, FiX } from 'react-icons/fi';
import { fetchStudentsWRTCourseType, setSelectedCourse, setSelectedStandard, getUsers, setSpecificUserId, setUserDetails, setRole, setStudentWithoutData, setUserids } from 'actions/userManagementV2';
import { setOthersSelectedTab } from 'actions/profileNew';
import { storeResourceData } from 'actions/feeManagement';
import { setSearchResultPageParamsObj } from 'actions/courseManagement';
import { getCrmDashboardData } from 'actions/marketPlaceCRM';
import { getStudentCourseList, getStudentResources } from 'actions/studentManagement';
import { Api } from 'services';
import { fluid } from 'components/UserDashboardV2/utils/responsive';
import PageHeader from 'components/pageHeader';
import PageDataLoader from 'components/pageDataLoader';
import illNoData from '../../Assets/ill-no-data.svg';
import img1 from './dashboardV3/Rectangle 148463.png';
import img2 from './dashboardV3/Rectangle 1484631.png';
import img3 from './dashboardV3/Rectangle 14846.png';
import img4 from './dashboardV3/Rectangle 1.png';
import img5 from './dashboardV3/Rectangle 1484.png';
import arrowSingleDown from './dashboardV3/arrow-single-down.svg';

const IMAGES_LIST = [img1, img2, img3, img4, img5];

const resolveCourseImage = (img, index) => {
    if (!img || img === 'default.png' || img === 'default.jpg') {
        return IMAGES_LIST[index % IMAGES_LIST.length];
    }
    if (typeof img === 'string') {
        const trimmed = img.trim();
        if (/^https?:\/\//i.test(trimmed) || trimmed.startsWith('blob:') || trimmed.startsWith('data:')) {
            return trimmed;
        }
        const s3Base = Api.dlS3Url || Api._s3_url || '';
        if (s3Base) {
            const baseEndsWithSlash = s3Base.endsWith('/');
            const pathStartsWithSlash = trimmed.startsWith('/');
            if (baseEndsWithSlash && pathStartsWithSlash) {
                return `${s3Base}${trimmed.slice(1)}`;
            }
            if (!baseEndsWithSlash && !pathStartsWithSlash) {
                return `${s3Base}/${trimmed}`;
            }
            return `${s3Base}${trimmed}`;
        }
        return trimmed;
    }
    return img;
};

const CourseCard = ({ course, history, pathname, skipsBatches, renderBadge, index = 0, onOpen }) => {
    const [isHovered, setIsHovered] = useState(false);

    const title = course.name || course.courseName || course.title || 'Course';
    const students = course.studentCount ?? course.students ?? 0;
    const batches = course.stdCount ?? course.batches ?? 0;
    // API sends these counts as strings ("1", "6"), so compare numerically.
    const studentsText = `${students} ${Number(students) === 1 ? 'student' : 'students'}`;
    const batchesText = `${batches} ${Number(batches) === 1 ? 'batch' : 'batches'}`;
    const mode = course.mode || 'Online';
    const resolvedImg = resolveCourseImage(course.image, index);
    const [imgSrc, setImgSrc] = useState(resolvedImg);

    useEffect(() => {
        setImgSrc(resolveCourseImage(course.image, index));
    }, [course.image, index]);

    const resourceTypeMap = {
        '/user-management/video-courses': 'videoResource',
        '/user-management/study-materials': 'studyMaterial',
        '/user-management/test-series': 'testSeries',
        '/user-management-v2/video-courses': 'videoResource',
        '/user-management-v2/study-materials': 'studyMaterial',
        '/user-management-v2/test-series': 'testSeries',
    };
    const currentResourceType = resourceTypeMap[pathname] || (pathname.includes('video-courses') ? 'videoResource' : pathname.includes('study-materials') ? 'studyMaterial' : pathname.includes('test-series') ? 'testSeries' : 'course');

    return (
        <div
            onClick={() => {
                onOpen?.();
                history.push(`${pathname}${skipsBatches ? '/student-list' : '/batches'}`, {
                    courseId: course.id || course._id,
                    courseName: title,
                    course,
                    courseType: currentResourceType,
                    resourceType: currentResourceType,
                });
            }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            style={{
                width: fluid(360),
                height: fluid(269),
                backgroundColor: 'rgb(255,255,255)',
                borderRadius: fluid(25),
                overflow: 'hidden',
                boxShadow: isHovered ? '0 8px 24px rgba(0,0,0,0.12)' : '0 4px 10px 4px rgba(0,0,0,0.04)',
                transition: 'box-shadow 0.2s ease',
                display: 'flex',
                flexDirection: 'column',
                boxSizing: 'border-box',
                cursor: 'pointer'
            }}
        >
            {/* Course Image */}
            <div style={{
                width: fluid(360),
                height: fluid(180),
                backgroundColor: 'rgb(217,217,217)',
                flexShrink: 0,
                overflow: 'hidden',
                position: 'relative'
            }}>
                <img
                    src={imgSrc}
                    alt={title}
                    onError={() => setImgSrc(IMAGES_LIST[index % IMAGES_LIST.length])}
                    style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        display: 'block'
                    }}
                />
            </div>

            {/* Card Details */}
            <div style={{
                padding: `${fluid(16)} ${fluid(20)}`,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                flex: 1,
                minHeight: 0
            }}>
                {/* Long names stop at 2 lines with "…" so the counts/badge stay
                    inside the card; the full name shows on hover — only when it
                    is actually cut off. */}
                <span
                    onMouseEnter={(e) => {
                        const el = e.currentTarget;
                        el.title = el.scrollHeight > el.clientHeight ? title : '';
                    }}
                    style={{
                        fontSize: fluid(16),
                        fontWeight: 400,
                        color: 'rgb(25,25,25)',
                        marginBottom: fluid(4),
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        wordBreak: 'break-word',
                        // Keep the full 2 lines — without this the flex column
                        // could squeeze the title and cut it mid-line, hiding the "…".
                        flexShrink: 0,
                    }}
                >
                    {title}
                </span>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}>
                    <span style={{ fontSize: fluid(14), fontWeight: 400, color: 'rgb(136,136,136)' }}>
                        {skipsBatches ? studentsText : `${studentsText} | ${batchesText}`}
                    </span>
                    {!skipsBatches && renderBadge(mode)}
                </div>
            </div>
        </div>
    );
};

const StudentCoursesV2 = ({ getStudentCourseList, getStudentResources }) => {
    const history = useHistory();
    const location = useLocation();

    // Determine course type page title based on url pathname
    const pathname = location.pathname;
    const pageTitleMap = {
        '/user-management/live-courses': 'Students classroom courses',
        '/user-management/video-courses': 'Students video courses',
        '/user-management/study-materials': 'Students study materials',
        '/user-management/test-series': 'Students test series',
        '/user-management-v2/live-courses': 'Students in classroom courses',
        '/user-management-v2/video-courses': 'Students in video courses',
        '/user-management-v2/study-materials': 'Students in study materials',
        '/user-management-v2/test-series': 'Students in test series',
    };
    const title = pageTitleMap[pathname] || (pathname.includes('video-courses') ? 'Students in video courses' : pathname.includes('study-materials') ? 'Students in study materials' : pathname.includes('test-series') ? 'Students in test series' : 'Students in classroom courses');

    const skipsBatches = pathname.includes('video-courses')
        || pathname.includes('study-materials')
        || pathname.includes('test-series');

    // State for filtering
    // Seeded from this history entry's state so coming back from a course
    // (history.goBack) shows the same search/tab as before.
    const [searchCourse, setSearchCourse] = useState(() => location.state?.searchCourse || '');
    const [searchMode, setSearchMode] = useState('All');
    const [activeTab, setActiveTab] = useState(() => location.state?.activeTab || 'ALL');
    const [apiCourses, setApiCourses] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const gridScrollRef = useRef(null);

    // The course grid scrolls on its own, so switching tabs would otherwise
    // keep the previous tab's scroll position; start each tab from the top.
    useEffect(() => {
        if (gridScrollRef.current) gridScrollRef.current.scrollTop = 0;
    }, [activeTab]);

    const fetchCoursesFromApi = useCallback(() => {
        setIsLoading(true);
        if (!skipsBatches) {
            const params = {};
            const selectedMode = activeTab !== 'ALL' ? activeTab : searchMode !== 'All' ? searchMode : null;
            if (selectedMode) {
                params.mode = selectedMode.toLowerCase();
            }
            getStudentCourseList?.(params, (response, error) => {
                setIsLoading(false);
                if (response && !error) {
                    const list = response?.courseStandards ?? (Array.isArray(response?.data?.courseStandards) ? response.data.courseStandards : (Array.isArray(response?.data) ? response.data : (Array.isArray(response) ? response : [])));
                    setApiCourses(list);
                }
            });
        } else {
            // Matches both /user-management/... and /user-management-v2/... paths
            // (the V2 paths weren't listed, so everything fell back to videoResource).
            const type = pathname.includes('study-materials') ? 'studyMaterial'
                : pathname.includes('test-series') ? 'testSeries'
                : 'videoResource';
            getStudentResources?.({ type }, (response, error) => {
                setIsLoading(false);
                if (response && !error) {
                    const list = response?.items
                        ?? response?.data?.items
                        ?? response?.courseStandards
                        ?? response?.resources
                        ?? (Array.isArray(response?.data?.items) ? response.data.items : [])
                        ?? (Array.isArray(response?.data?.courseStandards) ? response.data.courseStandards : [])
                        ?? (Array.isArray(response?.data?.resources) ? response.data.resources : [])
                        ?? (Array.isArray(response?.data) ? response.data : [])
                        ?? (Array.isArray(response) ? response : []);
                    setApiCourses(Array.isArray(list) ? list : []);
                }
            });
        }
    }, [pathname, skipsBatches, activeTab, searchMode, getStudentCourseList, getStudentResources]);

    useEffect(() => {
        fetchCoursesFromApi();
    }, [fetchCoursesFromApi]);

    const coursesToUse = apiCourses;

    // Stash the current search/tab on this page's history entry before
    // navigating away, so goBack() lands on the same filtered list.
    const rememberFilters = () => {
        history.replace({ ...location, state: { ...location.state, searchCourse, activeTab } });
    };

    // Filter courses based on search inputs
    const filteredCourses = coursesToUse.filter(course => {
        const titleStr = course.courseName || course.title || course.name || '';
        const matchesSearch = searchCourse.trim() === '' || titleStr.toLowerCase().includes(searchCourse.trim().toLowerCase());
        return matchesSearch;
    });

    const renderBadge = (mode) => {
        const m = (mode || '').toLowerCase();
        if (m === 'online') {
            return (
                <div style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgb(236,253,245)',
                    border: '1px solid rgb(52,211,153)',
                    borderRadius: fluid(4),
                    padding: `${fluid(6)} ${fluid(10)}`,
                    fontSize: fluid(12),
                    fontWeight: 600,
                    color: 'rgb(5,150,105)',
                    fontFamily: 'Figtree, Roboto, sans-serif',
                    boxSizing: 'border-box',
                    height: fluid(22)
                }}>
                    Online
                </div>
            );
        } else if (m === 'offline') {
            return (
                <div style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgb(254,242,242)',
                    border: '1px solid rgb(248,113,113)',
                    borderRadius: fluid(4),
                    padding: `${fluid(6)} ${fluid(10)}`,
                    fontSize: fluid(12),
                    fontWeight: 500,
                    color: 'rgb(220,38,38)',
                    fontFamily: 'Figtree, Roboto, sans-serif',
                    boxSizing: 'border-box',
                    height: fluid(22)
                }}>
                    Offline
                </div>
            );
        } else {
            return (
                <div style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgb(255,251,235)',
                    border: '1px solid rgb(251,191,36)',
                    borderRadius: fluid(4),
                    padding: `${fluid(6)} ${fluid(10)}`,
                    fontSize: fluid(12),
                    fontWeight: 500,
                    color: 'rgb(217,119,6)',
                    fontFamily: 'Figtree, Roboto, sans-serif',
                    boxSizing: 'border-box',
                    height: fluid(22)
                }}>
                    Hybrid
                </div>
            );
        }
    };

    const tabStyle = (tabKey, activeWidthPx) => {
        const isActive = activeTab === tabKey;
        return {
            width: fluid(activeWidthPx),
            height: fluid(45),
            borderRadius: fluid(12),
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            boxSizing: 'border-box',
            fontFamily: 'Roboto, sans-serif',
            fontSize: fluid(16),
            fontWeight: 700,
            textTransform: 'uppercase',
            transition: 'all 0.2s ease',
            backgroundColor: isActive ? 'rgb(89,64,153)' : 'transparent',
            color: isActive ? '#FFFFFF' : 'rgb(89,64,153)',
            border: isActive ? 'none' : '2px solid rgb(89,64,153)',
        };
    };

    return (
        <div style={{
            backgroundColor: 'transparent',
            height: '100%',
            width: 'calc(100% + 30px)',
            margin: -20,
            padding: '40px 20px 0px 24px',
            boxSizing: 'border-box',
            fontFamily: 'Roboto, sans-serif',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            marginTop: 0,
            position: 'relative',
        }}>
            <PageDataLoader visible={isLoading} />
            {/* Header + Search + Tabs (Sticky) */}
            <div style={{ flexShrink: 0, marginBottom: fluid(24) }}>
                <PageHeader
                    title={title}
                    onBackPress={() => history.goBack()}
                    actions={[
                        <div style={{ display: 'flex', alignItems: 'center', gap: fluid(24) }}>
                            <div style={{
                                position: 'relative',
                                width: fluid(428),
                                height: fluid(50),
                                display: 'flex',
                                alignItems: 'center',
                                borderRadius: fluid(12),
                                border: '1px solid rgb(174,174,174)',
                                backgroundColor: 'transparent',
                                padding: `0 ${fluid(16)}`,
                                boxSizing: 'border-box',
                                gap: fluid(10)
                            }}>
                                <FiSearch size={18} color="#AEAEAE" strokeWidth={1.5} style={{ flexShrink: 0 }} />
                                <input
                                    type="text"
                                    value={searchCourse}
                                    onChange={(e) => setSearchCourse(e.target.value)}
                                    placeholder="Search by course"
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        border: 'none',
                                        backgroundColor: 'transparent',
                                        color: '#191919',
                                        fontSize: fluid(16),
                                        padding: 0,
                                        boxSizing: 'border-box',
                                        outline: 'none',
                                        fontFamily: 'Roboto, sans-serif',
                                    }}
                                />
                                {searchCourse && (
                                    <button
                                        type="button"
                                        onClick={() => setSearchCourse('')}
                                        title="Clear search"
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            border: 'none',
                                            background: 'transparent',
                                            padding: 0,
                                            cursor: 'pointer',
                                            flexShrink: 0
                                        }}
                                    >
                                        <FiX size={18} color="#AEAEAE" strokeWidth={1.5} />
                                    </button>
                                )}
                            </div>
                        </div>
                    ]}
                />

                {/* Filter Tabs row — only shown for Live Courses where mode exists */}
                {!skipsBatches && (
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: fluid(16),
                        marginTop: fluid(20),
                        flexWrap: 'wrap'
                    }}>
                        <div onClick={() => setActiveTab('ALL')} style={tabStyle('ALL', 160)}>ALL</div>
                        <div onClick={() => setActiveTab('ONLINE')} style={tabStyle('ONLINE', 160)}>ONLINE</div>
                        <div onClick={() => setActiveTab('OFFLINE')} style={tabStyle('OFFLINE', 160)}>OFFLINE</div>
                        <div onClick={() => setActiveTab('HYBRID')} style={tabStyle('HYBRID', 160)}>HYBRID</div>
                    </div>
                )}
            </div>

            {/* Scrollable Course Grid */}
            <div ref={gridScrollRef} style={{
                flex: 1,
                minHeight: 0,
                overflowY: 'auto',
                overflowX: 'hidden',
                margin: '0 -24px',
                padding: '10px 24px',
                paddingBottom: fluid(30),
            }} className="dv2-chart-scrollbar">
                {!isLoading && filteredCourses.length === 0 ? (
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%',
                        minHeight: fluid(350),
                        width: '100%',
                    }}>
                        <img src={illNoData} style={{ width: fluid(250) }} alt="No courses found" />
                        <span style={{ fontSize: fluid(28), fontWeight: 600, color: '#191919', marginTop: fluid(16), fontFamily: 'Roboto, sans-serif' }}>
                            No courses found
                        </span>
                    </div>
                ) : (
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: `repeat(auto-fill, minmax(${fluid(340)}, 1fr))`,
                        gap: fluid(24),
                        width: '100%',
                        boxSizing: 'border-box'
                    }}>
                        {filteredCourses.map((course, idx) => (
                            <CourseCard
                                key={course.id || course._id || idx}
                                index={idx}
                                course={course}
                                history={history}
                                pathname={pathname}
                                skipsBatches={skipsBatches}
                                renderBadge={renderBadge}
                                onOpen={rememberFilters}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

const mapStateToProps = (state) => {
    const { user } = state.session;
    const { studentsDataWRTCourseType, usersList } = state.userManagementV2;
    const { crmDashboardData } = state.marketPlaceCRM;
    return { studentsDataWRTCourseType, usersList, user, crmDashboardData };
};

const mapDispatchToProps = (dispatch) => ({
    getStudentCourseList: (params, callback) => dispatch(getStudentCourseList(params, callback)),
    getStudentResources: (params, callback) => dispatch(getStudentResources(params, callback)),
    fetchStudentsWRTCourseType: (params, callback) => dispatch(fetchStudentsWRTCourseType(params, callback)),
    setSelectedCourse: res => dispatch(setSelectedCourse(res)),
    setSelectedStandard: res => dispatch(setSelectedStandard(res)),
    getUsers: (params, callback) => dispatch(getUsers(params, callback)),
    setSpecificUserId: id => dispatch(setSpecificUserId(id)),
    setUserDetails: res => dispatch(setUserDetails(res)),
    setOthersSelectedTab: (tab) => dispatch(setOthersSelectedTab(tab)),
    setRole: (key, val) => dispatch(setRole(key, val)),
    storeResourceData: res => dispatch(storeResourceData(res)),
    setStudentWithoutData: res => dispatch(setStudentWithoutData(res)),
    setUserids: userIds => dispatch(setUserids(userIds)),
    setSearchResultPageParamsObj: (key, val) => dispatch(setSearchResultPageParamsObj(key, val)),
    getCrmDashboardData: (startDate, endDate, callBack) => dispatch(getCrmDashboardData(startDate, endDate, callBack)),
});

export default connect(mapStateToProps, mapDispatchToProps)(StudentCoursesV2);
